import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { PageHeaderTabs } from "features/health-safety-forms";
import { getPageResults, useIsOnline, isStaffUser, canManageSupervisedTimesheets, useRouter, useTerminology } from "utils";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	Pagination,
	PaginationHeader,
	PaginationLoader,
	PopupSnackbar,
	PageHeader,
	PageHeaderListActions,
} from "frame/components";
import { connect, useSelector, useDispatch } from "react-redux";
import { retryQueueItem, removeQueueItem, sendRequested } from "features/queue";
import {
	ADD_TIMESHEET_ROUTE,
	ADD_ADMIN_TIMESHEET_ROUTE,
	NEW_LEAVE_PATH
} from "../timesheet-routes";
import { onAddAdminTimesheetReset } from "../timesheet-admin-actions";
import { getPagedTimesheetRecords } from "../../leave/leave-actions";
import { EditTimesheetForm } from "./edit-timesheet-form";
import { TimesheetCardCollapsed } from "./timesheet-card-collapsed";
import { LIST_TIMESHEETS_ROUTE } from "../timesheet-routes";
import {
	Grid,
	TextField,
	InputAdornment,
	Typography,
	Card,
	CardContent,
	IconButton,
	Button,
	CircularProgress,
	MenuItem
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import clsx from "clsx";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { initialTimesheetState } from "../timesheet-reducer";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import moment from "moment";
import { SITES_ROUTE } from "../../sites/actions/types";
import InfiniteScroll from "react-infinite-scroll-component";
import { onListRequested as onSitesListRequested } from "features/sites";
import { onActiveTimesheetRequest } from "../timesheet-actions";

const useStyles = makeStyles((theme) => ({
	filter: {
		"position": "relative",
		"right": 25,
		"top": 10,
		"marginTop": theme.spacing(0.1),
		"& .MuiOutlinedInput-input": {
			padding: theme.spacing(1.7, 2)
		},
		"& .MuiInputLabel-outlined": {
			transform: "translate(14px, 14px) scale(1)"
		},
		"& .MuiInputLabel-shrink": {
			transform: "translate(14px, -6px) scale(0.75)"
		},
		[theme.breakpoints.down("sm")]: {
			right: -100
		},
		[theme.breakpoints.down("xs")]: {
			top: 50,
			left: 20,
			minWidth: "100px"
		},
		"minWidth": "150px"
	},
	search: {
		display: "flex"
	},
	button: {
		"padding": theme.spacing(1.75, 3),
		"color": theme.palette.brand,
		"fontWeight": 600,
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(1, 3)
		},
		"background": theme.palette.highlight,
		"borderColor": theme.palette.highlight,
		"&:hover": {
			background: "#c1c72c",
			borderColor: "#c1c72c"
		},
		"width": "100%"
	},
	buttonSecondary: {
		"padding": theme.spacing(1.75, 3),
		"color": theme.palette.text.secondary,
		"fontWeight": 600,
		"&:hover": {
			color: theme.palette.brand
		},
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(1, 3)
		},
		"width": "100%"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
	filtersChip: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.white
	},
	chipsContainer: {
		marginTop: theme.spacing(1)
	},
	chipLabel: {
		color: theme.palette.text.tertiary
	},
	accordionSummarycontainer: {
		width: "100%",
		display: "flex",
		alignItems: "center"
	},
	accordionSummary: {
		marginRight: 10
	},
	dateGroup: {
		marginBottom: theme.spacing(3)
	},
	dateHeader: {
		fontSize: theme.typography.body2.fontSize,
		fontWeight: 600,
		marginBottom: theme.spacing(1),
		color: theme.palette.text.secondary,
		[theme.breakpoints.up("sm")]: {
			fontSize: theme.typography.subtitle1.fontSize,
			color: theme.palette.text.primary,
		},
	},
	quickRanges: {
		marginBottom: theme.spacing(1),
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
	},
	dateSummary: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		color: theme.palette.text.secondary,
		fontWeight: 500,
	},
	loadingContainer: {
		padding: theme.spacing(8, 3),
		textAlign: "center",
		minHeight: "200px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
	},
	buttonMenu: {
		marginBottom: theme.spacing(-3)
	},
	buttonGrid: {
		[theme.breakpoints.down("xs")]: {
			"flex-grow": 1,
		},
	},
	selectedButton: {
		"padding": theme.spacing(1, 4),
		"color": theme.palette.brand,
		"background": theme.palette.highlight,
		"borderColor": theme.palette.highlight,
		"fontWeight": 600,
		"&:hover": {
			background: "#c1c72c",
			borderColor: "#c1c72c"
		},
		[theme.breakpoints.down("xs")]: {
			width: "100%"
		}
	},
	buttonTab: {
		"padding": theme.spacing(1, 4),
		"color": theme.palette.text.secondary,
		"fontWeight": 600,
		"&:hover": {
			color: theme.palette.brand
		},
		[theme.breakpoints.down("xs")]: {
			width: "100%"
		}
	},
	addIcon: {
		marginRight: theme.spacing(1)
	},
	embeddedRoot: {
		display: "flex",
		flexDirection: "column",
		height: "min(70vh, 720px)",
		minHeight: 360,
		maxHeight: 900,
		overflow: "hidden",
	},
	embeddedPageHeader: {
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(1),
	},
}));

const mapStateToProps = ({ profile, timesheetFormReducer, organisations }) => ({
	timesheetForm: timesheetFormReducer,
	role: profile.role,
	currentStaffId: profile.staffId,
	supervisingStaffList: profile.supervisingStaffList,
	currentOrganisationGUID: organisations.currentOrganisation
});

const mapDispatchToProps = {};

export const TimesheetList = connect(
	mapStateToProps,
	mapDispatchToProps
)(({
	role,
	timesheetForm,
	currentOrganisationGUID,
	currentStaffId,
	supervisingStaffList,
	variant = "page",
	embeddedSiteName,
	embeddedEquipmentId,
}) => {
	const classes = useStyles();
	const canManageTimesheets = canManageSupervisedTimesheets(role, supervisingStaffList);
	// Timer entries are always available; the dedicated Timer Timesheet Entries flag has been removed.
	const timerTimesheetEntriesEnabled = true;
	const dispatch = useDispatch();
	const { siteLabel } = useTerminology();
	const timesheetApprovalsEnabled = useSelector(
		({ featureFlags }) => featureFlags?.timesheetApprovals === true
	);

	// Handlers for retry and cancel queue items
	const handleRetrySync = (queueItemId) => {
		dispatch(retryQueueItem(queueItemId));
		dispatch(sendRequested());
		setSnackMessage({
			message: "Retrying sync...",
			severity: "success"
		});
	};

	const handleDiscardSync = (queueItemId, isActiveTimesheet = false) => {
		if (queueItemId) {
			// Check if this is a start timesheet by looking at the queue item
			const queueItem = queueList.find((q) => q && q.id === queueItemId);
			const isStartTimesheet = queueItem && queueItem.url && /\/timesheet\/start$/i.test(queueItem.url);
			
			// Remove from queue
			dispatch(removeQueueItem(queueItemId));
			
			// If discarding a start timesheet, also clear the offline snapshot
			if (isStartTimesheet) {
				try {
					localStorage.removeItem("offline_active_timesheet");
				} catch (e) {
					// ignore storage errors
				}
			}
		} else if (isActiveTimesheet) {
			// For active timesheet with no queue item, just clear the localStorage snapshot
			try {
				localStorage.removeItem("offline_active_timesheet");
			} catch (e) {
				// ignore storage errors
			}
		}
		
		setSnackMessage({
			message: "Sync discarded",
			severity: "success"
		});
	};

	const handleStopTimesheet = (timesheetId) => {
		// Find the timesheet in the list
		const timesheet = timesheetList.find((t) => t.id === timesheetId);
		if (!timesheet) {
			setSnackMessage({
				message: "Timesheet not found",
				severity: "error"
			});
			return;
		}
		
		// Navigate to Review and Save Timesheet screen with the timesheet data
		// Use admin route and pass timesheet in state, with ?stopped=1 to enable review mode
		history.push(`${ADD_ADMIN_TIMESHEET_ROUTE}?stopped=1`, { 
			stopTimesheet: timesheet 
		});
	};

	const handleApproveTimesheet = (timesheetId) => {
		if (!currentOrganisationGUID || !timesheetId) {
			return;
		}
		safetyWiseApi
			.post(`${currentOrganisationGUID}/timesheet/approve/${timesheetId}`, {})
			.then((response) => {
				const updated = response?.data;
				if (updated?.id) {
					setTimesheetList((prev) =>
						prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t))
					);
				} else {
					getTimesheets(1, true);
				}
				setSnackMessage({
					message: "Timesheet approved",
					severity: "success",
				});
			})
			.catch((error) => {
				console.error("Error approving timesheet:", error);
				setSnackMessage({
					message: "Failed to approve timesheet",
					severity: "error",
				});
			});
	};

	const handleUnapproveTimesheet = (timesheetId) => {
		if (!currentOrganisationGUID || !timesheetId) {
			return;
		}
		safetyWiseApi
			.post(`${currentOrganisationGUID}/timesheet/unapprove/${timesheetId}`, {})
			.then((response) => {
				const updated = response?.data;
				if (updated?.id) {
					setTimesheetList((prev) =>
						prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t))
					);
				} else {
					getTimesheets(1, true);
				}
				setSnackMessage({
					message: "Timesheet unapproved",
					severity: "success",
				});
			})
			.catch((error) => {
				console.error("Error unapproving timesheet:", error);
				setSnackMessage({
					message: "Failed to unapprove timesheet",
					severity: "error",
				});
			});
	};
	const { match, history, location } = useRouter();
	const { siteId } = match.params;
	const isEmbedded = variant === "embedded";

	// Hooks must not be called conditionally; always select then safely derive values.
	const siteList = useSelector(({ sites }) => sites.pagination?.results || []);
	const hasNetwork = useSelector(({ queue }) => !!queue.hasNetwork);
	const siteNameFromId = siteId
		? siteList.find((site) => site.id == siteId)?.siteName || embeddedSiteName || ""
		: "";

	const [searchFilter, setSearchFilter] = useState("");
	const [approvalStatusFilter, setApprovalStatusFilter] = useState("all");
	const [showEditModal, setShowEditModal] = useState(false);
	const [selectedItemId, setSelectedItemId] = useState(null);
	const onEdit = (id) => () => {
		setSelectedItemId(id);
		setShowEditModal(true);
	};
	const onClone = (id) => () => {
		const source = timesheetList.find((t) => t.id === id);
		if (!source) {
			return;
		}
		// Pre-populate the admin form in Redux before navigating so the TimesheetForm
		// is visible on the very first render (avoids a blank screen on first clone attempt).
		const clonedStaffId = isStaffUser(role) && currentStaffId ? currentStaffId : (source.staffId || "");
		dispatch(onAddAdminTimesheetReset({
		siteId: source.siteId,
		organisationTaskTypeId: source.organisationTaskTypeId || "",
		equipment: source.equipment || [],
		timesheet: {
			staffId: clonedStaffId,
				startDate: source.startDateTimeUTC ? moment.utc(source.startDateTimeUTC).local().format("YYYY-MM-DD") : "",
				startTime: source.startDateTimeUTC ? moment.utc(source.startDateTimeUTC).local().format("HH:mm") : "",
				endDate: source.endDateTimeUTC ? moment.utc(source.endDateTimeUTC).local().format("YYYY-MM-DD") : "",
				endTime: source.endDateTimeUTC ? moment.utc(source.endDateTimeUTC).local().format("HH:mm") : "",
				lunchTime: source.lunchTimeMinutes ? String(source.lunchTimeMinutes) : "0",
				notes: source.notes || "",
				startLatitude: source.startLatitude || null,
				startLongitude: source.startLongitude || null,
				endLatitude: source.endLatitude || null,
			endLongitude: source.endLongitude || null,
			organisationTaskTypeId: source.organisationTaskTypeId || "",
		}
		}));
		history.push(ADD_ADMIN_TIMESHEET_ROUTE, { cloneTimesheet: source });
	};
	
	const theme = useTheme();
	const mobileView = useMediaQuery(theme.breakpoints.down("xs"));
	const [timesheetList, setTimesheetList] = useState(getPageResults(initialTimesheetState));
	const [isDoneFetching, setIsDoneFetching] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [showFilters, setShowFilters] = useState(false);
	const [isLoadingData, setIsLoadingData] = useState(false);
	const hasActiveTimesheet = timesheetForm.timesheet.toggle;
	const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(1);
	const [hasMoreTimesheets, setHasMoreTimesheets] = useState(true);
	const defaultPageSize = 20;
	const [snackMessage, setSnackMessage] = useState(null);
	const isComponentMountedRef = useRef(true);
	const prevNetworkRef = useRef(hasNetwork);
	const [useCollapsedView, setUseCollapsedView] = useState(true);
	const refreshListFlag = useSelector(({ timesheetReducer: ts }) => ts?.refreshList);

	// Track mount for async setState guards — only flip false on real unmount.
	// (Previously this was cleared in effects that re-run on hasNetwork, which
	// permanently blocked list updates after coming back online.)
	useEffect(() => {
		isComponentMountedRef.current = true;
		return () => {
			isComponentMountedRef.current = false;
		};
	}, []);

	// Prefetch sites so site-name lookups don't render as "-" during initial load (e.g. demos).
	useEffect(() => {
		// Don't fetch until we know the current organisation ID
		if (!currentOrganisationGUID) {
			return;
		}
		if (hasNetwork && siteList.length === 0) {
			dispatch(onSitesListRequested());
		}
	}, [dispatch, hasNetwork, siteList.length, currentOrganisationGUID]);

	// If this route is scoped to a site (/timesheets/:siteId), seed the search filter once the
	// site name is available (prevents crashing and avoids showing an unfiltered list).
	useEffect(() => {
		// Don't refetch until organisation is known
		if (!currentOrganisationGUID) {
			return;
		}
		if (siteId && siteNameFromId && searchFilter === "") {
			setSearchFilter(siteNameFromId);
		}
	}, [siteId, siteNameFromId, searchFilter, currentOrganisationGUID]);

	// Group timesheets by date
	const groupTimesheetsByDate = (timesheets) => {
		const grouped = {};
		const today = moment().startOf('day');
		const currentYear = moment().year();

		const formatDateLabel = (date) => {
			if (date.isSame(today, 'day')) {
				return 'Today';
			}
			const base = date.format('dddd D MMM');
			return date.year() === currentYear ? base : `${base}, ${date.year()}`;
		};

		timesheets.forEach(timesheet => {
			// Convert from UTC to local time before grouping so headers reflect NZDT/local
			const timesheetDate = moment.utc(timesheet.startDateTimeUTC).local().startOf('day');
			const dateKey = timesheetDate.format('YYYY-MM-DD');
			const dateLabel = formatDateLabel(timesheetDate);
			
			if (!grouped[dateKey]) {
				grouped[dateKey] = {
					label: dateLabel,
					timesheets: []
				};
			}
			grouped[dateKey].timesheets.push(timesheet);
		});
		
		// Sort by date (most recent first)
		return Object.keys(grouped)
			.sort((a, b) => moment(b).diff(moment(a)))
			.map(dateKey => ({
				dateKey,
				...grouped[dateKey]
			}));
	};

	// Ignore stale in-flight responses when a newer fetch has started (prevents
	// empty ↔ N-records flicker from overlapping requests).
	const fetchGenerationRef = useRef(0);
	const timesheetListRef = useRef(timesheetList);
	timesheetListRef.current = timesheetList;
	const totalNumberOfRecordsRef = useRef(totalNumberOfRecords);
	totalNumberOfRecordsRef.current = totalNumberOfRecords;
	const searchFilterRef = useRef(searchFilter);
	searchFilterRef.current = searchFilter;
	const approvalStatusFilterRef = useRef(approvalStatusFilter);
	approvalStatusFilterRef.current = approvalStatusFilter;
	const currentPageRef = useRef(currentPage);
	currentPageRef.current = currentPage;
	const currentOrganisationGUIDRef = useRef(currentOrganisationGUID);
	currentOrganisationGUIDRef.current = currentOrganisationGUID;

	const setNewTimesheetState = (timesheetResult, page, generation) => {
		if (!isComponentMountedRef.current) {
			return;
		}
		if (generation !== fetchGenerationRef.current) {
			return;
		}
		if (!timesheetResult || !Array.isArray(timesheetResult.results)) {
			return;
		}

		const pageResults = [...timesheetResult.results];
		if (page === 1) {
			const total = timesheetResult.totalNumberOfRecords ?? 0;
			// Ignore transient empty page-1 responses while we already have cards —
			// this is the empty ↔ "N records" flicker during offline→online sync.
			if (
				pageResults.length === 0 &&
				timesheetListRef.current.length > 0 &&
				total > 0
			) {
				setIsDoneFetching(true);
				return;
			}
			setTimesheetList(pageResults);
		} else {
			setTimesheetList([...timesheetListRef.current, ...pageResults]);
		}
		setIsDoneFetching(true);
	};

	const getTimesheets = (
		fromPageToRequest = currentPageRef.current,
		recordDeletedOrUpdated = false,
		pageSize = defaultPageSize,
		overrideFilters = {}
	) => {
		const organisationId = currentOrganisationGUIDRef.current;
		// Guard against missing organisation – avoids /api//timesheet URLs
		if (!organisationId) {
			if (isComponentMountedRef.current) {
				setIsDoneFetching(true);
				setIsLoadingData(false);
			}
			return;
		}

		const listLength = timesheetListRef.current.length;
		const totalRecords = totalNumberOfRecordsRef.current;
		// Already have everything loaded — skip unless this is an explicit refresh.
		if (!recordDeletedOrUpdated && totalRecords <= listLength) {
			if (isComponentMountedRef.current) {
				setHasMoreTimesheets(false);
				setIsDoneFetching(true);
			}
			return;
		}

		// Page-1 refreshes invalidate older in-flight requests (including page-2 races).
		// Pagination appends share the current generation so a newer page-1 can cancel them.
		const generation =
			fromPageToRequest === 1 ? ++fetchGenerationRef.current : fetchGenerationRef.current;
		const effectiveSearchFilter =
			overrideFilters.searchFilter !== undefined
				? overrideFilters.searchFilter
				: searchFilterRef.current;
		const effectiveApprovalStatus =
			overrideFilters.approvalStatusFilter !== undefined
				? overrideFilters.approvalStatusFilter
				: approvalStatusFilterRef.current;

		getPagedTimesheetRecords(organisationId, {
			ascending: false,
			orderBy: "isActive,startDateTimeUTC",
			page: fromPageToRequest,
			pageSize,
			searchKey: effectiveSearchFilter,
			...(embeddedEquipmentId ? { columnName: "organisationequipmentid", columnValue: embeddedEquipmentId } : {}),
			...(timesheetApprovalsEnabled &&
			effectiveApprovalStatus &&
			effectiveApprovalStatus !== "all"
				? { approvalStatus: effectiveApprovalStatus }
				: {}),
		})
			.then((result) => {
				if (!isComponentMountedRef.current) {
					return;
				}
				if (generation !== fetchGenerationRef.current) {
					return;
				}

				const data = result?.data;
				const results = Array.isArray(data?.results) ? data.results : null;
				if (!data || !results) {
					setIsDoneFetching(true);
					setIsLoadingData(false);
					return;
				}

				setNewTimesheetState(data, fromPageToRequest, generation);

				if (pageSize > defaultPageSize && fromPageToRequest === 1) {
					setCurrentPage(pageSize / defaultPageSize);
				} else {
					setCurrentPage(fromPageToRequest);
				}

				const total = data.totalNumberOfRecords ?? 0;
				setTotalNumberOfRecords(total);

				const loadedCount =
					fromPageToRequest === 1
						? results.length
						: timesheetListRef.current.length + results.length;
				setHasMoreTimesheets(loadedCount < total);
				setIsLoadingData(false);
			})
			.catch((error) => {
				console.error("Error fetching timesheets:", error);
				if (!isComponentMountedRef.current) {
					return;
				}
				if (generation !== fetchGenerationRef.current) {
					return;
				}
				setIsDoneFetching(true);
				setIsLoadingData(false);
			});
	};

	// Single load/refetch when route scope, role, or organisation changes.
	// Keep showing the previous list until new data arrives (no empty flash).
	useEffect(() => {
		if (siteId && !siteNameFromId && hasNetwork) {
			return;
		}
		if (!currentOrganisationGUID) {
			return;
		}

		setCurrentPage(1);
		// Only show the empty "Loading..." state when we have nothing to display yet.
		if (timesheetListRef.current.length === 0) {
			setIsDoneFetching(false);
		}
		getTimesheets(
			1,
			true,
			defaultPageSize,
			siteId ? { searchFilter: siteNameFromId } : {}
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [role, currentStaffId, currentOrganisationGUID, siteId, siteNameFromId]);

	// When network returns and stays up, refresh once. Mobile radios flap online/offline;
	// require a short stable online window so we don't reload the list on every blip.
	useEffect(() => {
		const wasOffline = !prevNetworkRef.current;
		prevNetworkRef.current = hasNetwork;
		if (!hasNetwork || !wasOffline) {
			return;
		}
		if (siteId && !siteNameFromId) {
			return;
		}
		const handle = setTimeout(() => {
			if (!isComponentMountedRef.current) return;
			// Abort if we dropped offline again during the grace period
			if (!prevNetworkRef.current) return;
			// Reconcile Active Timesheet flag with server after reconnect
			dispatch(onActiveTimesheetRequest());
			if (timesheetListRef.current.length === 0) {
				setIsDoneFetching(false);
			}
			getTimesheets(1, true, defaultPageSize, siteId ? { searchFilter: siteNameFromId } : {});
		}, 1500);
		return () => clearTimeout(handle);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasNetwork, siteId, siteNameFromId]);

	// Redux refresh flag (toggled after queue sync / edit save)
	const isInitialRefreshMount = useRef(true);
	useEffect(() => {
		if (isInitialRefreshMount.current) {
			isInitialRefreshMount.current = false;
			return;
		}
		if (!hasNetwork) {
			return;
		}
		if (timesheetListRef.current.length === 0) {
			setIsDoneFetching(false);
		}
		getTimesheets(1, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshListFlag]);

	// Debounced search — keep current cards visible until the new result set arrives
	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			setCurrentPage(1);
			if (timesheetListRef.current.length === 0) {
				setIsDoneFetching(false);
			}
			getTimesheets(1, true);
		}, 500);

		return () => clearTimeout(searchTimeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchFilter]);

	useEffect(() => {
		if (!timesheetApprovalsEnabled) {
			return;
		}
		setCurrentPage(1);
		if (timesheetListRef.current.length === 0) {
			setIsDoneFetching(false);
		}
		getTimesheets(1, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [approvalStatusFilter, timesheetApprovalsEnabled]);

	const ref = useRef(null);
	const [height, setHeight] = useState(0);

	// The InfiniteScroll Component requires a height for it to work  effectively and for it resize relative to the parent.
useEffect(() => {
		const handleResize = () => {
        if (!isComponentMountedRef.current) { return; }
        setHeight(ref.current.offsetHeight - 20); // reduce height by 20px ottherwise the scrollbar flows past the height of screen.
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Prepare lists for collapsed view: active first, then closed grouped by date
	// Build pending items from queue and offline snapshot
	const queueList = useSelector(({ queue }) => queue.list) || [];
	// (hasNetwork already declared above)

	// Pending closed items (stop queued)
	const pendingClosed = queueList
		.filter((q) => q && q.url && /\/timesheet\/stop$/i.test(q.url))
		.map((q) => {
			const failedCount = q.failedCount || 0;
			const maxRetries = q.maxRetries || 5;
			const isPermanentFailure = q.isPermanentFailure || false;
			return {
				id: `pending-${q.id}`,
				siteId: q.body?.siteId,
				staffId: q.body?.staffId,
				startDateTimeUTC: q.body?.startDateTimeUTC, // we enrich this when enqueuing stop
				endDateTimeUTC: q.body?.endDateTimeUTC,
				lunchTimeMinutes: q.body?.lunchTimeMinutes,
				notes: q.body?.notes,
				organisationTaskTypeId: q.body?.organisationTaskTypeId,
				isActive: false,
				pendingSync: !isPermanentFailure && failedCount < maxRetries,
				syncFailed: isPermanentFailure || failedCount >= maxRetries,
				failedCount: failedCount,
				lastError: q.lastError || null,
				queueItemId: q.id,
			};
		});

	// Pending active item (start queued with snapshot in localStorage)
	let pendingActive = [];
	try {
		const raw = localStorage.getItem("offline_active_timesheet");
		if (raw) {
			const snap = JSON.parse(raw);
			if (snap && snap.startDate && snap.startTime) {
				// Find corresponding queue item for start
				const startQueueItem = queueList.find((q) => q && q.url && /\/timesheet\/start$/i.test(q.url));
				const startFailedCount = startQueueItem?.failedCount || 0;
				const startMaxRetries = startQueueItem?.maxRetries || 5;
				const startIsPermanentFailure = startQueueItem?.isPermanentFailure || false;
				// If there's no queue item but there's a snapshot, it means it might have synced but snapshot wasn't cleared
				// or it's pending. We'll show it as pending sync so user can discard it
				const hasPendingSync = !startQueueItem || (!startIsPermanentFailure && startFailedCount < startMaxRetries);
				pendingActive.push({
					id: `pending-active`,
					siteId: snap.siteId,
					staffId: timesheetForm.timesheet.staffId,
					startDateTimeUTC: moment(`${snap.startDate} ${snap.startTime}`, "YYYY-MM-DD HH:mm").toISOString(),
					isActive: true,
					organisationTaskTypeId: snap.organisationTaskTypeId,
					startLatitude: snap.startLatitude,
					startLongitude: snap.startLongitude,
					pendingSync: hasPendingSync || !startQueueItem, // Show as pending if no queue item (allows discard)
					syncFailed: startQueueItem && (startIsPermanentFailure || startFailedCount >= startMaxRetries),
					failedCount: startFailedCount,
					lastError: startQueueItem?.lastError || null,
					queueItemId: startQueueItem?.id,
					hasLocalStorageSnapshot: true, // Mark that this has a localStorage snapshot
				});
			}
		}
	} catch (e) {
		// ignore
	}

	// If there is an active timesheet toggle in state but no local snapshot,
	// render a lightweight active placeholder so the list never appears empty
	// during online transitions while the server list is still loading.
	// Do NOT show the placeholder once the server has confirmed the timesheet ID
	// (via ACTIVE_TIMESHEET_RECEIVED) – the real record will already be in timesheetList.
	if (
		pendingActive.length === 0 &&
		timesheetForm?.timesheet?.toggle &&
		!timesheetForm?.timesheet?.id &&
		timesheetForm?.timesheet?.startDate &&
		timesheetForm?.timesheet?.startTime
	) {
		const startQueueItem = queueList.find((q) => q && q.url && /\/timesheet\/start$/i.test(q.url));
		const placeholderFailedCount = startQueueItem?.failedCount || 0;
		const placeholderMaxRetries = startQueueItem?.maxRetries || 5;
		const placeholderIsPermanentFailure = startQueueItem?.isPermanentFailure || false;
		pendingActive.push({
			id: `active-placeholder`,
			siteId: timesheetForm.siteId,
			staffId: timesheetForm.timesheet.staffId,
			startDateTimeUTC: moment(`${timesheetForm.timesheet.startDate} ${timesheetForm.timesheet.startTime}`, "YYYY-MM-DD HH:mm").toISOString(),
			isActive: true,
			organisationTaskTypeId: timesheetForm.organisationTaskTypeId,
			startLatitude: timesheetForm.timesheet.startLatitude,
			startLongitude: timesheetForm.timesheet.startLongitude,
			// Show pending chip only if a start is still queued and we are offline
			pendingSync: startQueueItem && !hasNetwork && !placeholderIsPermanentFailure && placeholderFailedCount < placeholderMaxRetries,
			syncFailed: startQueueItem && (placeholderIsPermanentFailure || placeholderFailedCount >= placeholderMaxRetries),
			failedCount: placeholderFailedCount,
			lastError: startQueueItem?.lastError || null,
			queueItemId: startQueueItem?.id,
			hasLocalStorageSnapshot: false, // This is just a placeholder, not a real snapshot
		});
	}

	const augmentedList = [...pendingActive, ...pendingClosed, ...timesheetList];

	const activeTimesheets = augmentedList.filter((t) => t.isActive);
	const inactiveTimesheets = augmentedList.filter((t) => !t.isActive);

	const listBody = (
			<div
				ref={ref}
				style={{
					backgroundColor: "",
					height: "100%",
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					flex: isEmbedded ? 1 : undefined,
					minHeight: isEmbedded ? 0 : undefined,
				}}
			>
				{/* <p>
					{totalNumberOfRecords} {timesheetList.length}
				</p> */}
				{/* n.o of records and length of loaded list */}

				<InfiniteScroll
					dataLength={augmentedList.length}
					next={() => {
						getTimesheets(currentPage + 1);
					}}
					hasMore={hasMoreTimesheets && isDoneFetching}
					loader={<CardText message={"Loading..."} />}
					height={height + "px"}
					scrollThreshold={0.99}
					style={{ paddingBottom: "10px" }}
				>
					{!isEmbedded && <ScrollToTopOnMount />}
											<PageHeader
							className={isEmbedded ? classes.embeddedPageHeader : undefined}
							title="Timesheets"
							hideTitle={isEmbedded}
							backActionPath={siteId ? `${SITES_ROUTE}/${siteId}` : LIST_TIMESHEETS_ROUTE}
							titleAdornment={
						!isEmbedded ? (
							<PageHeaderListActions
								showFilters={showFilters}
								onToggleFilters={() => setShowFilters(!showFilters)}
								showCollapseToggle
								useCollapsedView={useCollapsedView}
								onToggleCollapsedView={() => setUseCollapsedView(!useCollapsedView)}
							/>
						) : null
					}
						>
							{/* START: Search */}
							{showFilters && (
								<div className={classes.gridContainer}>
									<Grid container spacing={2} className={classes.filterGrid}>
										<Grid item xs={12} md={timesheetApprovalsEnabled ? 6 : 6}>
											<TextField
												className={classes.search}
												id="input-with-icon-textfield"
												variant="outlined"
												label="Search"
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													),
													endAdornment: searchFilter && (
														<InputAdornment position="end">
															<IconButton
																aria-label="clear search"
																onClick={() => {
																setSearchFilter("");
																setCurrentPage(1);
																getTimesheets(1, true, undefined, {
																	searchFilter: ""
																});
																}}
																edge="end"
																size="small"
															>
																<ClearIcon />
															</IconButton>
														</InputAdornment>
													)
												}}
												value={searchFilter}
												onChange={(val) => {
													const { value } = val.target;
													setSearchFilter(value);
													// Real-time search with debounce effect will handle the API call
												}}
												style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
												placeholder={`e.g ${siteLabel} Name, Notes, Staff Name`}
											/>
										</Grid>
										{timesheetApprovalsEnabled && (
											<Grid item xs={12} md={4}>
												<TextField
													select
													variant="outlined"
													label="Approval"
													value={approvalStatusFilter}
													onChange={(event) => {
														setApprovalStatusFilter(event.target.value);
													}}
													style={{ backgroundColor: "white", width: "100%", maxWidth: 280 }}
												>
													<MenuItem value="all">All</MenuItem>
													<MenuItem value="approved">Approved</MenuItem>
													<MenuItem value="pending">Pending</MenuItem>
												</TextField>
											</Grid>
										)}
									</Grid>
								</div>
							)}
							{/* END: Search */}
					
					{/* Buttons */}
					{!isEmbedded && (
					<Grid container className={classes.buttonMenu} spacing={3} alignItems="center">
						<Grid item className={classes.buttonGrid}>
							<Button
								className={(location.pathname === ADD_TIMESHEET_ROUTE || location.pathname.startsWith(`${ADD_TIMESHEET_ROUTE}/`) || (location.pathname === ADD_ADMIN_TIMESHEET_ROUTE || location.pathname.startsWith(`${ADD_ADMIN_TIMESHEET_ROUTE}/`))) ? classes.selectedButton : classes.buttonTab}
								variant="contained"
								component={RouterLink}
								to={siteId ? `${ADD_TIMESHEET_ROUTE}/${siteId}` : ADD_TIMESHEET_ROUTE}
							>
								{!hasActiveTimesheet && <AddIcon className={classes.addIcon} />}
								{hasActiveTimesheet ? "Active Timesheet" : "Timesheet"}
							</Button>
						</Grid>
						{canManageTimesheets && (
							<Grid item className={classes.buttonGrid}>
								<Button
									className={(location.pathname === ADD_ADMIN_TIMESHEET_ROUTE || location.pathname.startsWith(`${ADD_ADMIN_TIMESHEET_ROUTE}/`)) ? classes.selectedButton : classes.buttonTab}
									variant="contained"
									component={RouterLink}
									to={siteId ? `${ADD_ADMIN_TIMESHEET_ROUTE}/${siteId}` : ADD_ADMIN_TIMESHEET_ROUTE}
								>
									<AddIcon className={classes.addIcon} />
									Timesheet (Admin)
								</Button>
							</Grid>
						)}
					</Grid>
					)}
					</PageHeader>

					<PageContent pad>
						{isLoadingData ? (
							<div className={classes.loadingContainer}>
								<CircularProgress size={50} style={{ marginBottom: "20px", color: theme.palette.brand }} />
								<Typography variant="h5" style={{ marginBottom: "15px", fontWeight: 600 }}>
									Loading Timesheet Data
								</Typography>
								<Typography variant="body1" style={{ color: "#666" }}>
									Please wait while we fetch your timesheet data...
								</Typography>
							</div>
						) : (
							<>
								<p style={{ marginTop: 5 }}>
									{augmentedList.length > 0 ? `${augmentedList.length} records` : "No records loaded"}
								</p>
								<div>
								{augmentedList.length > 0 && (
							useCollapsedView ? (
								// Collapsed grouped view with Active section on top
								<>
									{activeTimesheets.length > 0 && (
										<div className={classes.dateGroup}>
											<Typography className={classes.dateHeader}>Active</Typography>
											{activeTimesheets.map((timesheet, index) => (
												<TimesheetCardCollapsed
													key={`active-${index}`}
													timesheet={timesheet}
													onEdit={onEdit(timesheet.id)}
													onDelete={(id) => {
														safetyWiseApi
															.del(`${currentOrganisationGUID}/timesheet/delete/${id}`)
															.then(() => {
																setSnackMessage({
																	message: "Timesheet deleted successfully",
																	severity: "success"
																});
																getTimesheets(1, true);
															})
															.catch(error => {
																console.error("Error deleting timesheet:", error);
																setSnackMessage({
																	message: "Failed to delete timesheet",
																	severity: "error"
																});
															});
												}}
												role={role}
														hideActions={true}
													supervisingStaffList={supervisingStaffList}
													onRetrySync={timesheet.queueItemId ? () => handleRetrySync(timesheet.queueItemId) : undefined}
													onDiscardSync={(timesheet.queueItemId || timesheet.hasLocalStorageSnapshot) ? () => handleDiscardSync(timesheet.queueItemId, true) : undefined}
													onEndTimesheet={canManageTimesheets ? handleStopTimesheet : undefined}
											/>
											))}
										</div>
									)}

									{groupTimesheetsByDate(inactiveTimesheets).map((dateGroup) => (
										<div key={dateGroup.dateKey} className={classes.dateGroup}>
											<Typography className={classes.dateHeader}>
												{dateGroup.label}
											</Typography>
											{dateGroup.timesheets.map((timesheet, index) => (
												<TimesheetCardCollapsed
													key={`${dateGroup.dateKey}-${index}`}
													timesheet={timesheet}
													onEdit={onEdit(timesheet.id)}
													onClone={onClone(timesheet.id)}
													onRetrySync={timesheet.queueItemId ? () => handleRetrySync(timesheet.queueItemId) : undefined}
													onDiscardSync={(timesheet.queueItemId || timesheet.hasLocalStorageSnapshot) ? () => handleDiscardSync(timesheet.queueItemId, true) : undefined}
													onDelete={(id) => {
														safetyWiseApi
															.del(`${currentOrganisationGUID}/timesheet/delete/${id}`)
															.then(() => {
																setSnackMessage({
																	message: "Timesheet deleted successfully",
																	severity: "success"
																});
																getTimesheets(1, true);
															})
															.catch(error => {
																console.error("Error deleting timesheet:", error);
																setSnackMessage({
																	message: "Failed to delete timesheet",
																	severity: "error"
																});
															});
												}}
												role={role}
												supervisingStaffList={supervisingStaffList}
												onApprove={handleApproveTimesheet}
												onUnapprove={handleUnapproveTimesheet}
											/>
											))}
										</div>
									))}
								</>
									) : (
										<>
											{activeTimesheets.length > 0 && (
												<div className={classes.dateGroup}>
													<Typography className={classes.dateHeader}>Active</Typography>
													{activeTimesheets.map((timesheet, index) => (
														<TimesheetCardCollapsed
															key={`active-${index}`}
															timesheet={timesheet}
															onEdit={onEdit(timesheet.id)}
															onDelete={(id) => {
																safetyWiseApi
																	.del(`${currentOrganisationGUID}/timesheet/delete/${id}`)
																	.then(() => {
																		setSnackMessage({
																			message: "Timesheet deleted successfully",
																			severity: "success"
																		});
																		getTimesheets(1, true);
																	})
																	.catch(error => {
																		console.error("Error deleting timesheet:", error);
																		setSnackMessage({
																			message: "Failed to delete timesheet",
																			severity: "error"
																		});
																	});
															}}
															role={role}
															supervisingStaffList={supervisingStaffList}
															hideActions={true}
															forceExpanded={true}
															onRetrySync={timesheet.queueItemId ? () => handleRetrySync(timesheet.queueItemId) : undefined}
															onDiscardSync={(timesheet.queueItemId || timesheet.hasLocalStorageSnapshot) ? () => handleDiscardSync(timesheet.queueItemId, true) : undefined}
															onEndTimesheet={canManageTimesheets ? handleStopTimesheet : undefined}
														/>
													))}
												</div>
											)}

											{groupTimesheetsByDate(inactiveTimesheets).map((dateGroup) => (
												<div key={dateGroup.dateKey} className={classes.dateGroup}>
													<Typography className={classes.dateHeader}>
														{dateGroup.label}
													</Typography>
													{dateGroup.timesheets.map((timesheet, index) => (
														<TimesheetCardCollapsed
															key={`${dateGroup.dateKey}-${index}`}
															timesheet={timesheet}
															onEdit={onEdit(timesheet.id)}
															onClone={onClone(timesheet.id)}
															onDelete={(id) => {
																safetyWiseApi
																	.del(`${currentOrganisationGUID}/timesheet/delete/${id}`)
																	.then(() => {
																		setSnackMessage({
																			message: "Timesheet deleted successfully",
																			severity: "success"
																		});
																		getTimesheets(1, true);
																	})
																	.catch(error => {
																		console.error("Error deleting timesheet:", error);
																		setSnackMessage({
																			message: "Failed to delete timesheet",
																			severity: "error"
																		});
																	});
															}}
															role={role}
															forceExpanded={true}
															supervisingStaffList={supervisingStaffList}
															onApprove={handleApproveTimesheet}
															onUnapprove={handleUnapproveTimesheet}
															onRetrySync={timesheet.queueItemId ? () => handleRetrySync(timesheet.queueItemId) : undefined}
															onDiscardSync={(timesheet.queueItemId || timesheet.hasLocalStorageSnapshot) ? () => handleDiscardSync(timesheet.queueItemId, true) : undefined}
														/>
													))}
												</div>
											))}
										</>
									)
								)}
								</div>
							</>
						)}
					</PageContent>
					<EditTimesheetForm
						open={showEditModal}
						id={selectedItemId}
						timesheetList={timesheetList}
						currentOrganisationGUID={currentOrganisationGUID}
						onClose={() => {
							const timesheetIndex = timesheetList.findIndex((t) => t.id === selectedItemId); // find timesheetIndex
							//timesheetIndex + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
							const pageSizeToLoad =
								Math.ceil((timesheetIndex + 1) / defaultPageSize) * defaultPageSize;

							// Get timesheets first page to the place the user was at before editing (using pageSizetoLoad),
							getTimesheets(1, true, pageSizeToLoad);

							setShowEditModal(false);
						}}
					/>
					{!isLoadingData && !isDoneFetching && augmentedList.length === 0 && <CardText message={"Loading..."} style={{ marginTop: "20px" }} />}
					{!isLoadingData && !hasMoreTimesheets && isDoneFetching && <CardText message={"All timesheets have been loaded."} />}
					{!isLoadingData && augmentedList.length === 0 && isDoneFetching && (
						<CardText message={"No timesheets to show."} />
					)}
				</InfiniteScroll>
				<PopupSnackbar 
					message={snackMessage?.message || timesheetForm.errorMessage} 
					severity={snackMessage?.severity || (timesheetForm.errorMessage ? "error" : undefined)}
					coloured={snackMessage?.severity === "error" || !!timesheetForm.errorMessage} 
					error={snackMessage?.severity === "error" || !!timesheetForm.errorMessage} 
					onExited={() => setSnackMessage(null)} 
				/>
			</div>
	);

	if (isEmbedded) {
		return <div className={classes.embeddedRoot}>{listBody}</div>;
	}

	return (
		<Page className={classes.root} title="Timesheets" style={{ height: "100%" }}>
			{listBody}
		</Page>
	);
});

export const CardText = ({ message, style }) => {
	const theme = useTheme();
	const useCardTextStyles = makeStyles((theme) => ({
		cardContent: {
			padding: theme.spacing(2),
			flexGrow: 1,
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			[theme.breakpoints.down("sm")]: {
				width: "100%",
				flexWrap: "wrap"
			}
		}
	}));
	const classes = useCardTextStyles();
	return (
		<Card
			style={{
				display: "flex",
				alignItems: "center",
				flexWrap: "wrap",
				position: "relative",
				margin: theme.spacing(0, 3, 3, 3),
				...style
			}}
		>
			<CardContent className={classes.cardContent}>
				<p>{message}</p>
			</CardContent>
		</Card>
	);
};
